import type { FloorId } from '@/data/floors';
import { finnNpc, type NpcDef } from '@/data/npcs';
import { formaterKlokkeslett } from '@/systems/gameClock';

/**
 * NPC-timeplaner (fase 3 + 5, Mad TV-mekanikk): karakterer finnes på
 * bestemte steder til bestemte tider. Rom-scenene spawner/despawner
 * NPC-er ut fra GameClock — aldri hardkod tider i scener.
 *
 * Timeplanene er bevisst lagt med overlapp (Thomas i resepsjonen og
 * Kristine i direksjonen 10:30–11:30) så spilleren må prioritere.
 * NPC-definisjonene bor i data/npcs.ts.
 */

export { NPCER, finnNpc, type NpcDef } from '@/data/npcs';

/** 'heis' er et pseudo-rom: vinduene der rivalen kan stå i heisen (5B). */
export type TimeplanRom = FloorId | 'heis';

export interface TimeplanOppfoering {
  rom: TimeplanRom;
  /** Minutter siden midnatt, innenfor spilldagen 08:00–17:00. */
  fraMinutt: number;
  tilMinutt: number;
  /** 0 = mandag … 6 = søndag. Udefinert = alle dager. */
  ukedag?: number;
}

export interface NpcTimeplan {
  npcId: string;
  oppforinger: TimeplanOppfoering[];
}

const kl = (timer: number, minutter = 0): number => timer * 60 + minutter;

export const TIMEPLANER: NpcTimeplan[] = [
  {
    npcId: 'agent-thomas',
    oppforinger: [
      // Hverdager i resepsjonen — overlapper med Kristine i direksjonen.
      { rom: 'resepsjon', fraMinutt: kl(10), tilMinutt: kl(12), ukedag: 0 },
      { rom: 'resepsjon', fraMinutt: kl(10), tilMinutt: kl(12), ukedag: 1 },
      { rom: 'resepsjon', fraMinutt: kl(10), tilMinutt: kl(12), ukedag: 2 },
      { rom: 'resepsjon', fraMinutt: kl(10), tilMinutt: kl(12), ukedag: 3 },
      { rom: 'resepsjon', fraMinutt: kl(10), tilMinutt: kl(12), ukedag: 4 },
    ],
  },
  {
    npcId: 'kristine-mentor',
    oppforinger: [
      // Konflikt-vinduet: 10:30–11:30 i direksjonen mens Thomas står i
      // resepsjonen — fire etasjer unna.
      { rom: 'direksjon', fraMinutt: kl(10, 30), tilMinutt: kl(11, 30) },
      { rom: 'kantine', fraMinutt: kl(12), tilMinutt: kl(12, 30) },
    ],
  },
  {
    npcId: 'kritiker-aftenposten',
    oppforinger: [
      // Bare tirsdag og torsdag — den som vil pleie kritikeren må følge med.
      { rom: 'bokhandel', fraMinutt: kl(14), tilMinutt: kl(15), ukedag: 1 },
      { rom: 'bokhandel', fraMinutt: kl(14), tilMinutt: kl(15), ukedag: 3 },
    ],
  },
  // ---- Kapelle Damm (fase 5) ----
  {
    npcId: 'kapelle-resepsjonist',
    oppforinger: [
      // Alltid på post i KD-resepsjonen.
      { rom: 'kapelleResepsjon', fraMinutt: kl(8), tilMinutt: kl(17) },
    ],
  },
  {
    npcId: 'kapelle-rival',
    oppforinger: [
      // Heisvinduene (5B): her kan spilleren «tilfeldigvis» møte rivalen.
      { rom: 'heis', fraMinutt: kl(8, 30), tilMinutt: kl(9) },
      { rom: 'heis', fraMinutt: kl(12), tilMinutt: kl(12, 30) },
      // Eget kontor og møterom er låste rom — oppføringene brukes til
      // hint («hvor er rivalen nå»), ikke til spawning i spillbare rom.
      { rom: 'kapelleResepsjon', fraMinutt: kl(11), tilMinutt: kl(12) },
    ],
  },
  {
    npcId: 'kapelle-misfornoyd',
    oppforinger: [
      // 5D: dukker opp i kantina i lunsjen — relasjonsbygging.
      { rom: 'kantine', fraMinutt: kl(11, 30), tilMinutt: kl(12, 30) },
    ],
  },
];

/** Står NPC-en i heisen akkurat nå (rivalens heisvinduer, 5B)? */
export function erIHeisen(npcId: string, minutt: number, dag: number): boolean {
  const plan = TIMEPLANER.find((t) => t.npcId === npcId);
  if (!plan) return false;
  const ukedag = ukedagForDag(dag);
  return plan.oppforinger.some(
    (o) =>
      o.rom === 'heis' &&
      (o.ukedag === undefined || o.ukedag === ukedag) &&
      minutt >= o.fraMinutt &&
      minutt < o.tilMinutt,
  );
}

/** Spilldag 1 er en mandag. */
export function ukedagForDag(dag: number): number {
  return ((dag - 1) % 7 + 7) % 7;
}

function aktivOppfoering(
  o: TimeplanOppfoering,
  minutt: number,
  ukedag: number,
): boolean {
  if (o.ukedag !== undefined && o.ukedag !== ukedag) return false;
  return minutt >= o.fraMinutt && minutt < o.tilMinutt;
}

/** Hvilke NPC-er står i rommet akkurat nå? */
export function npcIRom(rom: FloorId, minutt: number, dag: number): NpcDef[] {
  const ukedag = ukedagForDag(dag);
  const ut: NpcDef[] = [];
  for (const plan of TIMEPLANER) {
    const aktiv = plan.oppforinger.some(
      (o) => o.rom === rom && aktivOppfoering(o, minutt, ukedag),
    );
    if (!aktiv) continue;
    const npc = finnNpc(plan.npcId);
    if (npc) ut.push(npc);
  }
  return ut;
}

/** Hvor er NPC-en akkurat nå? Null hvis utenfor alle vinduer. */
export function hvorErNpc(
  npcId: string,
  minutt: number,
  dag: number,
): TimeplanOppfoering | null {
  const plan = TIMEPLANER.find((t) => t.npcId === npcId);
  if (!plan) return null;
  const ukedag = ukedagForDag(dag);
  return plan.oppforinger.find((o) => aktivOppfoering(o, minutt, ukedag)) ?? null;
}

/**
 * Hint-tekst for et tomt rom: «Agent Thomas er her 10:00–12:00».
 * Vises kun etter at spilleren har møtt NPC-en (flagges i GameState).
 */
export function timeplanHintFor(npcId: string, rom: FloorId): string | null {
  const plan = TIMEPLANER.find((t) => t.npcId === npcId);
  const npc = finnNpc(npcId);
  if (!plan || !npc) return null;
  const iRommet = plan.oppforinger.filter((o) => o.rom === rom);
  if (iRommet.length === 0) return null;
  const forste = iRommet[0];
  return `${npc.navn} er her ${formaterKlokkeslett(forste.fraMinutt)}–${formaterKlokkeslett(forste.tilMinutt)}`;
}

/** Flagg-navn for «spilleren har møtt NPC-en» — lagres i GameState.flags. */
export function mottFlaggFor(npcId: string): string {
  return `npc_mott_${npcId}`;
}
